import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, signal, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { email, FieldTree, form, minLength, required, validate } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'

import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { LaskuChangeNoopCollector, LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuSpostiService } from '@lemon/core-firebase/service/lasku/lasku-sposti.service'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { StringService } from '@lemon/core/service/string.service'

import { LaskuEmailEsikatselutiedot } from '../_jaettu-angular/laskut/esikatselu/email.perinteinen.component'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'

import { LaskuService } from '../_angular/service/lasku/lasku.service'

import { NewFeature } from '@lemon/core-firebase/model/kayttaja'
import {
  EmailLahetysStatus,
  EmailLahetysStatusKoodi,
  Lasku,
  LaskuasetuksetSpostille,
  LaskuBase,
  LaskunLahetysdata,
  LaskunLahetystapa,
  LaskunSahkoinenOsoite,
  LaskunumeroTyyppi,
} from '@lemon/core-firebase/model/lasku'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { UserSettingsService } from 'app/_angular/service/user-settings.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { debounceTime, map, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators'
import { LaskuAjastaDialog, LaskuAjastaDialogData } from './dialogit/lasku-ajasta.dialog'

export interface LaskunLahetystiedot {
  error: any
  tapa: LaskunLahetystapa
  lahetettyJuuri: Lasku
  lahetettyKasiteltava: LaskuBase
}

interface SahkoinenLomake {
  osoite: string
  valittaja: string | null
}

interface KuluLomake {
  huomautuskulu: number | null
  viivastyskorko: boolean
}

interface EmailLomake {
  aihe: string
  otsikko: string
  teksti: string
  lisaavastaanottaja: string
}

@Component({
  selector: 'app-laheta-component',
  templateUrl: './laheta.component.html',
  styleUrls: ['./laheta.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LahetaComponent implements OnInit, OnDestroy {
  private spostiasetukset: LaskuasetuksetSpostille = null
  private tilaaEsikatseluPaivitysSubject: ReplaySubject<number> = new ReplaySubject(1)
  private ngUnsubscribe = new Subject<void>()

  @Input() onToistuva: boolean = false
  @Output() laskuLahetettiin = new EventEmitter<LaskunLahetystiedot>()
  @Output() laskuAjastettiin = new EventEmitter<void>()
  @Output() tallenna = new EventEmitter<void>()

  private _aktiivinenValilehti: number = 0
  get aktiivinenValilehti(): number {
    return this._aktiivinenValilehti
  }
  @Input()
  set aktiivinenValilehti(indeksi: number) {
    this._aktiivinenValilehti = indeksi
  }

  private _data: LaskunLahetysdata
  get data(): LaskunLahetysdata {
    return this._data
  }
  @Input()
  set data(data: LaskunLahetysdata) {
    this._data = data
    this._lahetysdata.set(data)
    this.onkoMuistutus = !!this._data?.muistutus
    this.onkoHyvitys = this._data?.kasiteltava?.nrotyyppi === LaskunumeroTyyppi.HYVITYS
    this.onkoUudelleenlahetys = !!this._data?.uudelleenlahetys
    this.alustaTiedot(this.onkoMuistutus, data)
  }

  onkoMuistutus: boolean = false
  onkoHyvitys: boolean = false
  onkoUudelleenlahetys: boolean = false

  get voikoAjastaa(): boolean {
    return !this.onkoMuistutus && !this.onkoHyvitys && !this.onkoUudelleenlahetys
  }

  get infotekstinAvain(): string {
    if (this.onkoHyvitys) {
      return 'info-hyvitys'
    }
    return this.onkoMuistutus ? 'info-muistutus' : 'info-tavallinen'
  }

  esikatselutiedot: ReplaySubject<LaskuEmailEsikatselutiedot> = new ReplaySubject(1)
  verkkolaskuoperaattorit: Verkkolaskuoperaattori[]
  emailLomake: FieldTree<EmailLomake>
  sahkoinenLomake: FieldTree<SahkoinenLomake>
  kuluLomake: FieldTree<KuluLomake>
  asetuksissaOnOletustietojaVirhe = false
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this._validationService.updateValidationStatus('lasku.sposti', this.emailLomake)
  })
  osoitehaunHintTeksti: string = ''
  // osoitehaunTulokset: ApixHaeVerkkolaskuosoiteTulos[] = null
  showNewFeatureSignObservable: Observable<boolean>

  korko: number = 0
  viivastyskorkoLisataan: boolean = false

  private _buttonPushInflight: boolean = false
  private readonly _lahetysdata = signal<LaskunLahetysdata>(null)
  private readonly _vastaanottajatPuuttuvat = signal(false)
  peppolValidationObservable: BehaviorSubject<{ error: boolean; message: string }> = new BehaviorSubject(null)

  email = {
    aihe: '',
    otsikko: '',
    teksti: '',
    vastaanottajat: [],
  }

  sahkoinen = {
    ytunnus: '',
    osoite: '',
    valittaja: '',
  }

  pyydaPaivitys() {
    if (this.aktiivinenValilehti === 0) {
      this.tilaaEsikatseluPaivitysSubject.next(1)
    }
  }

  annaOsoitteenValidointiFunktio(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        const ctrlValue = this._stringService.removeAllWhiteSpaces(control.value)

        if (this._lahetysdata().kasiteltava?.asiakas?.nimi?.toLowerCase()?.split(' ')?.includes('apix')) {
          // Special exception to enable sending invoices to Apix
          const apixTunnus = '003723327487'
          const apixValittuValittajana = this.sahkoinenLomake.valittaja().value()?.trim() === apixTunnus

          if (apixValittuValittajana && control.value === apixTunnus) {
            return null
          }
        }

        const valittajat = this.verkkolaskuoperaattorit || []
        for (const valittaja of valittajat) {
          const valittajaTunnus = this._stringService.removeAllWhiteSpaces(valittaja.tunnus)
          if (valittajaTunnus === ctrlValue) {
            return { valittaja: true }
          }
        }
      }
      return null
    }
  }

  constructor(
    private _validationService: FormValidationService,
    private _laskuSpostiService: LaskuSpostiService,
    private _laskuService: LaskuService,
    private _errorHandler: ErrorHandler,
    private _laskuSharedService: LaskuSharedService,
    private _ladataanService: LadataanService,
    private _timestampService: TimestampService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _laskuKorkoService: LaskuKorkoService,
    private _stringService: StringService,
    private _dialog: MatDialog,
    private _lemonTranslation: LemonTranslationService,
    private _router: Router,
    private _userSettingsService: UserSettingsService,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this.sahkoinenLomake = form(signal<SahkoinenLomake>({ osoite: '', valittaja: '' }), polku => {
      required(polku.osoite)
      validoiReaktiivisesti(polku.osoite, this.annaOsoitteenValidointiFunktio())
      minLength(polku.osoite, 9, { when: ({ valueOf }) => valueOf(polku.valittaja)?.trim() === 'PEPPOL' })
      required(polku.valittaja)
    })

    this.kuluLomake = form(signal<KuluLomake>({ huomautuskulu: 0, viivastyskorko: this.viivastyskorkoLisataan }), polku => {
      required(polku.huomautuskulu)
      validoiReaktiivisesti(polku.viivastyskorko, Validators.required)
    })

    this.emailLomake = form(signal<EmailLomake>({ aihe: this.email.aihe, otsikko: this.email.otsikko, teksti: this.email.teksti, lisaavastaanottaja: '' }), polku => {
      required(polku.aihe)
      required(polku.otsikko)
      required(polku.teksti)
      email(polku.lisaavastaanottaja, { when: ({ value }) => !this._vastaanottajatPuuttuvat() && !!value()?.trim() })
      validate(polku.lisaavastaanottaja, () => (this._vastaanottajatPuuttuvat() ? { kind: 'min' } : null))
    })
    const osoiteKentta = this.sahkoinenLomake.osoite
    const valittajaKentta = this.sahkoinenLomake.valittaja

    this.verkkolaskuoperaattorit = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()

    // this.sahkoinenForm.get('ytunnus').valueChanges.pipe(
    //   debounceTime(1000)
    // ).subscribe((value: string) => {
    //   if (value) {
    //     const kasitelty = this.validationService.processValue(value)
    //     this.osoitehaunHintTeksti = 'Haetaan verkkolaskuosoitetta...'

    //     const pyynto: ApixHaeVerkkolaskuosoitettaPyynto = {
    //       ytunnus: kasitelty
    //     }

    //     const callable = this.lemontreeFunctions.httpsCallable<ApixHaeVerkkolaskuosoitettaPyynto, ApixHaeVerkkolaskuosoitettaVastaus>('apixHaeVerkkolaskuosoite')
    //     callable(pyynto).then(tulos => {

    //       if (tulos && tulos.tulokset && tulos.tulokset.length > 0) {
    //         this.osoitehaunTulokset = tulos.tulokset
    //       } else {
    //         this.osoitehaunTulokset = null
    //       }
    //       this.osoitehaunHintTeksti = ''

    //     }).catch(error => {
    //       this.osoitehaunHintTeksti = 'Haun aikana tapahtui virhe.'
    //       this.errorHandler.handleError(error)
    //     })
    //   }
    // })
    // this.sahkoinenForm.get('ytunnus').valueChanges.subscribe(value => {
    //   this.sahkoinen.ytunnus = value
    // })

    combineLatest([arvonMuutokset(osoiteKentta), arvonMuutokset(valittajaKentta)])
      .pipe(
        debounceTime(500),
        switchMap(([osoite, valittaja]) => {
          if (valittaja !== 'PEPPOL') {
            return of(null)
          }

          if (!osoite || osoite.trim().length < 9) {
            return of(null)
          }

          return from(this._laskuService.validoiPeppolOsoite(osoite, this._lemonTranslation.nykyinenKieli)).pipe(
            startWith({ error: false, message: this._lemonTranslation.lokalisoi('lasku.peppol-validaatio.tarkistetaan', this._lemonTranslation.nykyinenKieli) }),
          )
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(value => {
        this.peppolValidationObservable.next(value)
      })

    arvonMuutokset(osoiteKentta)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sahkoinen.osoite = value?.trim() ?? null
      })

    arvonMuutokset(valittajaKentta)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        this.sahkoinen.valittaja = value?.trim() ?? null
        // Reaktiivinen lomake lähetti osoitteen arvon uudelleen, kun välittäjän vaihtuminen validoi osoitteen; Peppol-tarkistus käynnistyy siitä
        asetaArvo(osoiteKentta, osoiteKentta().value())
      })

    // Kulu form
    arvonMuutokset(this.kuluLomake.huomautuskulu).subscribe(value => {
      if (this.data.huomautuskulu) {
        this.data.huomautuskulu.hinta = value
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    })
    arvonMuutokset(this.kuluLomake.viivastyskorko).subscribe(value => {
      this.viivastyskorkoLisataan = value
      if (this.data.korkokulu) {
        this.data.korkokulu.hinta = this.viivastyskorkoLisataan ? this.korko : 0
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    })

    // Email form
    arvonMuutokset(this.emailLomake.aihe).subscribe(value => {
      this.email.aihe = value || ''
      this.data.kasiteltava.email.aihe = this.email.aihe
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.otsikko).subscribe(value => {
      this.email.otsikko = value || ''
      this.data.kasiteltava.email.otsikko = this.email.otsikko
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.teksti).subscribe(value => {
      this.email.teksti = value || ''
      this.data.kasiteltava.email.teksti = this.email.teksti
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    arvonMuutokset(this.emailLomake.lisaavastaanottaja).subscribe(value => {
      this._vastaanottajatPuuttuvat.set(false)
      if (value && (value.indexOf(',') !== -1 || value.indexOf(' ') !== -1)) {
        this.lisaaEmail(this.emailLomake.lisaavastaanottaja)
      }
    })

    this.tilaaEsikatseluPaivitysSubject.pipe(takeUntil(this.ngUnsubscribe), throttleTime(250)).subscribe(() => {
      if (this.data.asetukset && this.data.juurilasku && this.data.kasiteltava) {
        this._laskuSharedService.paivitaRyppaanJaKasiteltavanSummat(this.data.juurilasku, this.data.kasiteltava, this.data.reskontra, new LaskuChangeNoopCollector())
        this.esikatselutiedot.next({
          asetukset: this.data.asetukset,
          juurilasku: this.data.juurilasku,
          kasiteltava: this.data.kasiteltava,
          maksumuistutus: this.onkoMuistutus,
        })
      }
    })
  }

  // private _filter(value: string | Verkkolaskuoperaattori): Verkkolaskuoperaattori[] {
  //   if (value === '' || !value) {
  //     return []
  //   }
  //   let filterValue = ''
  //   if (value instanceof String || typeof value === 'string') {
  //     filterValue = value.toLowerCase()
  //   } else {
  //     filterValue = value && value.nimi ? value.nimi.toLowerCase() : ''
  //   }
  //   return this._sahkoisenLaskunValittajaService.annaKaikkiValittajat().filter(operaattori => operaattori.nimi.toLowerCase().includes(filterValue) || operaattori.tunnus.toLowerCase().includes(filterValue))
  // }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  ngOnInit() {
    this.tilaaEsikatseluPaivitysSubject.next(1)

    this.showNewFeatureSignObservable = this._userSettingsService.userSettingsObservable.pipe(map(settings => !settings?.seen?.[NewFeature.TOISTUVAT_LASKUT]))
  }

  private _annaOletuspohjanAvain(onkoMuistutus: boolean): string {
    if (this.onkoHyvitys) {
      return 'hyvityssposti'
    }
    return onkoMuistutus ? 'muistutussposti' : 'sposti'
  }

  private alustaTiedot(onkoMuistutus: boolean, data: LaskunLahetysdata) {
    // Tyhjennä lomakkeet:
    this.email = {
      aihe: '',
      otsikko: '',
      teksti: '',
      vastaanottajat: [],
    }
    this.sahkoinen = {
      ytunnus: '',
      osoite: '',
      valittaja: '',
    }

    if (!data.asetukset) {
      return
    }

    // Alusta email
    if (!data.asetukset.spostiasetukset) {
      data.asetukset.spostiasetukset = {}
    }

    this.spostiasetukset = this._laskuSpostiService.annaSpostiAsetukset(data.kasiteltava, onkoMuistutus, data.asetukset)

    const oletuspohja = this._annaOletuspohjanAvain(onkoMuistutus)
    this.email.aihe = this.korvaaMuuttujat(this.spostiasetukset.aihe || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.aihe`, data.kasiteltava))
    this.email.otsikko = this.korvaaMuuttujat(
      this.spostiasetukset.otsikko || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.otsikko`, data.kasiteltava),
    )
    this.email.teksti = this.korvaaMuuttujat(this.spostiasetukset.teksti || this._laskuSharedService.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.teksti`, data.kasiteltava))

    if (data.kasiteltava.asiakas.laskunVastaanottajat) {
      for (const vastaanottaja of data.kasiteltava.asiakas.laskunVastaanottajat) {
        this.email.vastaanottajat.push(vastaanottaja)
      }
    }

    if (!data.kasiteltava.email) {
      data.kasiteltava.email = {
        start: this._timestampService.now(),
        done: null,
        aihe: '',
        otsikko: '',
        teksti: '',
        slogan: '',
        template: null,
        vastaanottajat: null,
      }
    }
    data.kasiteltava.email.aihe = this.email.aihe
    data.kasiteltava.email.otsikko = this.email.otsikko
    data.kasiteltava.email.teksti = this.email.teksti
    data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    data.kasiteltava.email.footer = 'footer2'
    data.kasiteltava.email.slogan = this.data.asetukset.slogan || ''

    asetaArvo(this.emailLomake.aihe, this.email.aihe)
    asetaArvo(this.emailLomake.otsikko, this.email.otsikko)
    asetaArvo(this.emailLomake.teksti, this.email.teksti)

    // Alusta sähköinen lasku
    this.sahkoinen.ytunnus = data.kasiteltava.asiakas.ytunnus ? data.kasiteltava.asiakas.ytunnus : ''

    if (!data.kasiteltava.asiakas.sahkoinenosoite) {
      data.kasiteltava.asiakas.sahkoinenosoite = {
        sahkoinenOsoite: '',
        sahkoinenValittaja: '',
      }
    }

    if (!data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite) {
      data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite = ''
    }

    if (!data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja) {
      data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja = ''
    }

    this.sahkoinen.osoite = data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite
    asetaArvo(this.sahkoinenLomake.osoite, data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenOsoite)

    const unconfirmedValittajaTunnus = data.kasiteltava.asiakas.sahkoinenosoite.sahkoinenValittaja
    const confirmedValittajaTunnus = unconfirmedValittajaTunnus ? this._parseAndConfirmValittajaTunnus(unconfirmedValittajaTunnus) : null
    this.sahkoinen.valittaja = confirmedValittajaTunnus
    asetaArvo(this.sahkoinenLomake.valittaja, confirmedValittajaTunnus)

    // Alusta korkokulut
    if (onkoMuistutus && !this.onkoUudelleenlahetys) {
      this.korko = this._laskuKorkoService.laskeKorkoLaskulle(data.juurilasku, data.kasiteltava, data.asetukset, data.reskontra)
      this.data.korkokulu.hinta = this.viivastyskorkoLisataan ? this.korko : 0
      this.data.huomautuskulu.hinta = 0
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  lisaa() {
    this._vastaanottajatPuuttuvat.set(false)
    this.lisaaEmail(this.emailLomake.lisaavastaanottaja)
  }

  private lisaaEmail(field: FieldTree<string>) {
    const value = field().value()
    // console.log('raaka', field.value)
    if ((value || '').trim()) {
      let arvo = value.trim()
      arvo = arvo.replace(/,/g, '').trim()
      // console.log('kasitelty', arvo)
      if (!Validators.email({ value: arvo } as AbstractControl)) {
        this.email.vastaanottajat.push(arvo)
        asetaArvo(field, '')
        this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
        this.tilaaEsikatseluPaivitysSubject.next(1)
      }
    }
  }

  poistaAsiakas(vastaanottaja: string): void {
    const index = this.email.vastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.email.vastaanottajat.splice(index, 1)
    }
    this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  private muodostaVastaanottaja() {
    const vastaanottajat: EmailLahetysStatus[] = []
    for (const vastaanottaja of this.email.vastaanottajat) {
      vastaanottajat.push({
        email: vastaanottaja.trim().toLowerCase(),
        status: EmailLahetysStatusKoodi.PROSESSOIDAAN,
        viesti: null,
      })
    }
    return vastaanottajat
  }

  private async validoiSahkoinenLomake(): Promise<boolean> {
    if (!this.sahkoinen || !this.sahkoinen.osoite || !this.sahkoinenLomake().valid()) {
      const formErrors = [this.sahkoinenLomake.osoite, this.sahkoinenLomake.valittaja].filter(kentta => kentta().errors().length > 0)
      const peppolErrorObject = await firstValueFrom(this.peppolValidationObservable)
      const peppolError = !!peppolErrorObject?.error
      if ((!peppolError && formErrors.length > 0) || (peppolError && formErrors.length > 1)) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.sahkoinenLomake)
      }
      return false
    }
    return true
  }

  async lahetaSahkoinen() {
    const validoi = await this.validoiSahkoinenLomake()
    if (!validoi) {
      return
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      // Päivitä tiedot
      // this.data.kasiteltava.asiakas.ytunnus = this.sahkoinen.ytunnus

      this._ladataanService.aloitaLataaminen()

      if (this.onkoUudelleenlahetys) {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .lahetaUudelleenSahkoisesti(osoite, this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .merkitseLaskuLahetetyksiSahkoisesti(osoite, this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        const osoite: LaskunSahkoinenOsoite = { sahkoinenOsoite: this.sahkoinen.osoite, sahkoinenValittaja: this.sahkoinen.valittaja }
        await this._laskuService
          .merkitseLaskuLahetetyksiSahkoisesti(osoite, this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOINEN, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  async saveAndPrint() {
    this._ladataanService.aloitaLataaminen()

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      if (this.onkoUudelleenlahetys) {
        await this._laskuService
          .lahetaUudelleenTulostamalla(this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        await this._laskuService
          .merkitseLaskunMaksumuistutusLahetetyksiTulostamalla(this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        await this._laskuService
          .merkitseLaskuLahetetyksiTulostamalla(this.data.juurilasku, this.data.kasiteltava)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.ITSE, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  async send() {
    if (!this.email || !this.email.vastaanottajat || this.email.vastaanottajat.length < 1) {
      this._vastaanottajatPuuttuvat.set(true)
      return
    }

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true
    try {
      this._ladataanService.aloitaLataaminen()
      const vastaanottajat: EmailLahetysStatus[] = this.muodostaVastaanottaja()

      const postmarkTemplateId = this._laskuSpostiService.annaSahkopostipohjanPostmarkNumero(this.spostiasetukset.template, this.data.kasiteltava.kieli)

      if (this.onkoUudelleenlahetys) {
        await this._laskuService
          .lahetaUudelleenSpostilla(postmarkTemplateId, this.data.juurilasku, this.data.kasiteltava, vastaanottajat, this.email.aihe, this.email.otsikko, this.email.teksti, this.data.asetukset.slogan)
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else if (this.onkoMuistutus) {
        await this._laskuService
          .merkitseLaskunMaksumuistutusLahetetyksiSpostilla(
            postmarkTemplateId,
            this.data.juurilasku,
            this.data.kasiteltava,
            vastaanottajat,
            this.email.aihe,
            this.email.otsikko,
            this.email.teksti,
            this.data.asetukset.slogan,
            true,
          )
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      } else {
        await this._laskuService
          .merkitseLaskuLahetetyksiSpostilla(
            postmarkTemplateId,
            this.data.juurilasku,
            this.data.kasiteltava,
            vastaanottajat,
            this.email.aihe,
            this.email.otsikko,
            this.email.teksti,
            this.data.asetukset.slogan,
          )
          .then(({ juurilasku, kasiteltava }) => {
            this.data.juurilasku = juurilasku
            this.data.kasiteltava = kasiteltava
            this.laskuLahetettiin.next({ error: null, tapa: LaskunLahetystapa.SAHKOPOSTI, lahetettyJuuri: juurilasku, lahetettyKasiteltava: kasiteltava })
          })
          .catch(err => {
            this._errorHandler.handleError(err)
            this.laskuLahetettiin.next({ error: err, tapa: null, lahetettyJuuri: null, lahetettyKasiteltava: null })
          })
      }
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }

  private korvaaMuuttujat(korvattava: string): string {
    return this._laskuSpostiService.korvaaMuuttujat(korvattava, this.data.asetukset, this.data.juurilasku, this.data.kasiteltava)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  // This form field used to be a free-text input, so extra validation is needed here to catch invalid operator ID-s in legacy data.
  private _parseAndConfirmValittajaTunnus(unconfirmed: string) {
    if (!unconfirmed) {
      return null
    }
    if (unconfirmed['tunnus']) {
      return unconfirmed['tunnus']
    }
    if (!unconfirmed['toUpperCase']) {
      return null
    }
    const upperCase = unconfirmed.toUpperCase()
    const found: Verkkolaskuoperaattori = this._sahkoisenLaskunValittajaService.annaValittaja(upperCase)
    return found?.tunnus ?? null
  }

  private async _updateToistuvatLaskutFeatureSeen() {
    const userSettings = await firstValueFrom(this._userSettingsService.userSettingsObservable)
    if (!userSettings?.seen?.[NewFeature.TOISTUVAT_LASKUT]) {
      const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
      if (kayttajanTiedot?.asiakasAvain) {
        this._userSettingsService.setFeatureSeen(NewFeature.TOISTUVAT_LASKUT, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._firebaseLemonaid)
      }
    }
  }

  async ajasta() {
    await this._updateToistuvatLaskutFeatureSeen()

    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    try {
      const validoi = await this._validoiLahetystapa()
      if (!validoi) {
        return
      }
      const dialogData: LaskuAjastaDialogData = {
        lasku: this.data.juurilasku,
      }
      const dialogRef = this._dialog.open(LaskuAjastaDialog, { data: dialogData, width: '600px' })
      const asetukset = await firstValueFrom(dialogRef.afterClosed())

      if (asetukset) {
        try {
          this._ladataanService.aloitaLataaminen()

          this._asetaToistuvanLaskunLahetystapa(this.aktiivinenValilehti)
          this._laskuService.paivitaTuotteidenJaAsiakkaanAvaimet(this.data.juurilasku, null)

          await this._laskuService.tallennaLuonnos(this.data.juurilasku, this.data.kasiteltava, asetukset)
          this.laskuAjastettiin.emit()
          this._router.navigate(['/laskutus/toistuvat-laskut'])
        } catch (err) {
          this._errorHandler.handleError(err)
        } finally {
          this._ladataanService.lopetaLataaminen()
        }
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._buttonPushInflight = false
    }
  }

  private async _validoiLahetystapa(): Promise<boolean> {
    if (this.aktiivinenValilehti === 0) {
      if (!this.email || !this.email.vastaanottajat || this.email.vastaanottajat.length < 1) {
        this._vastaanottajatPuuttuvat.set(true)
        return false
      }
      if (!this.emailLomake().valid()) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.emailLomake)
        return false
      }
      return true
    } else if (this.aktiivinenValilehti === 1) {
      return await this.validoiSahkoinenLomake()
    }
    return true
  }

  private _asetaToistuvanLaskunLahetystapa(valilehti: number) {
    if (valilehti === 0) {
      this._laskuService.asetaToistuvanLaskunEmailTiedot(this.data.juurilasku, this.data.kasiteltava)
    } else if (valilehti === 1) {
      const osoite: LaskunSahkoinenOsoite = {
        sahkoinenOsoite: this.sahkoinen.osoite,
        sahkoinenValittaja: this.sahkoinen.valittaja,
      }
      this._laskuService.asetaToistuvanLaskunSahkoinenTiedot(this.data.juurilasku, osoite)
    } else if (valilehti === 2) {
      this._laskuService.asetaToistuvanLaskunPrintTiedot(this.data.juurilasku)
    }
  }
}
